import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Code,
  FileInput,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Spoiler,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconPaperclip, IconPhoto, IconSend, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { pomanjsaj, pomanjsajVse } from '../api/slike';
import { PrejPotem } from '../vozila/PrejPotem';
import { NapakaApi, sporocilo } from '../api/napaka';
import { stevilo, tocke, trajanje } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { KLJUC_AI } from './ai';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';

type Preverjanje = Shema['AiPreverjanje'];
type Funkcija = Shema['AiFunkcija'];

async function klic<T>(k: () => Promise<T>): Promise<T> {
  try {
    return await k();
  } catch (e) {
    obNapaki(e);
    throw e;
  }
}

function izbireModelov(
  modeli: Preverjanje | undefined,
  se: (string | undefined)[] = [],
  vrsta: 'besedilo' | 'slike' | 'vsi' = 'besedilo',
) {
  const ponujeni = [
    ...(vrsta !== 'slike' ? (modeli?.modeli ?? []) : []),
    ...(vrsta !== 'besedilo' ? (modeli?.modeliSlik ?? []) : []),
  ];
  const m = ponujeni.map((x) => ({
    value: x.id ?? '',
    label: `${x.naziv ?? x.id} (${x.id})${vrsta === 'vsi' && x.slike ? ' – slike' : ''}`,
  }));
  for (const id of se) {
    if (id && !m.some((x) => x.value === id)) {
      m.unshift({ value: id, label: id });
    }
  }
  return m;
}

/**
 * Skrbništvo → Umetna inteligenca → Cenik: cena modelov na 1M žetonov, kot jo objavi Google (USD, obračunamo kot EUR).
 * Brez cene izbranega modela stranke umetne inteligence ne morejo uporabljati; stranki se zaračuna strošek z maržo in
 * DDV (nastavitev strežnika).
 */
export function CenikAi({ izbrani, modeli }: { izbrani?: string; modeli?: Preverjanje }) {
  const odjemalec = useQueryClient();
  const cenik = useQuery({
    queryKey: [...KLJUC_AI, 'cenik'],
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai/cenik', { headers: glave() }))),
  });
  // marža in DDV (nastavitev strežnika) sta samo v skrbništvu, strankam se ne prikazujeta
  const povezava = useQuery({
    queryKey: KLJUC_AI,
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai', { headers: glave() }))),
  });
  const [model, setModel] = useState<string | null>(null);
  const [vhod, setVhod] = useState<number | string>('');
  const [izhod, setIzhod] = useState<number | string>('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const izbraniModel = model ?? izbrani ?? null;
  const brezCene = izbrani && cenik.data && !cenik.data.some((c) => c.model === izbrani);

  const uredi = (c: Shema['AiCena']) => {
    setModel(c.model ?? null);
    setVhod(c.vhod ?? '');
    setIzhod(c.izhod ?? '');
  };

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      const c = await podatki(
        api.PUT('/api/skrbnik/ai/cenik', {
          headers: glave(),
          body: {
            model: izbraniModel ?? undefined,
            vhod: typeof vhod === 'number' ? vhod : undefined,
            izhod: typeof izhod === 'number' ? izhod : undefined,
          },
        }),
      );
      odjemalec.setQueryData([...KLJUC_AI, 'cenik'], c);
      setModel(null);
      setVhod('');
      setIzhod('');
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  const izbrisi = async (m: string) => {
    setNapaka(null);
    try {
      odjemalec.setQueryData(
        [...KLJUC_AI, 'cenik'],
        await podatki(
          api.DELETE('/api/skrbnik/ai/cenik/{model}', { headers: glave(), params: { path: { model: m } } }),
        ),
      );
    } catch (e) {
      obNapaki(e);
      setNapaka(sporocilo(e));
    }
  };

  return (
    <Kartica naslov="Cenik modelov">
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Stack gap="sm">
          {brezCene && (
            <Alert color="red" variant="light" role="alert">
              Cena izbranega modela {izbrani} ni vpisana – stranke umetne inteligence ne morejo uporabljati.
            </Alert>
          )}
          {!cenik.data ? (
            <StanjeNalaganja
              napaka={cenik.error}
              ponovi={() => void cenik.refetch()}
              naslov="Cenika ni bilo mogoče naložiti"
            />
          ) : cenik.data.length === 0 ? (
            <Text size="sm" c="dimmed">
              Cene še niso vpisane.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={420}>
              <Table verticalSpacing={6} highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Model</Table.Th>
                    <Table.Th ta="right">Vhod</Table.Th>
                    <Table.Th ta="right">Izhod</Table.Th>
                    <Table.Th>Spremenil</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {cenik.data.map((c) => (
                    <Table.Tr key={c.model} style={{ cursor: 'pointer' }} onClick={() => uredi(c)}>
                      <Table.Td>
                        <Text size="sm" ff="monospace" fw={c.model === izbrani ? 700 : undefined}>
                          {c.model}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">{c.vhod}</Table.Td>
                      <Table.Td ta="right">{c.izhod}</Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {c.spremenil} {cas(c.spremenjeno)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Tooltip label="Izbriši ceno">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Izbriši ceno ${c.model}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void izbrisi(c.model!);
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          <Text size="xs" c="dimmed">
            Cene v USD na 1M žetonov (1 USD obračunamo kot 1 EUR). Stranki se zaračuna strošek
            {povezava.data ? ` + ${povezava.data.marza} % marže + ${povezava.data.ddv} % DDV` : ' z maržo in DDV'},
            takoj ob vsakem klicu (gibanje »Umetna inteligenca« v točkah). Marža se strankam ne prikazuje.
          </Text>
        </Stack>
        <Stack gap="sm">
          <Select
            size="md"
            label="Model"
            data={izbireModelov(modeli, [izbrani, model ?? undefined], 'vsi')}
            value={izbraniModel}
            onChange={setModel}
            searchable
            error={napake.model}
          />
          <SimpleGrid cols={2} spacing="sm">
            <NumberInput
              size="md"
              label="Vhod (USD / 1M)"
              description="Besedilo, slike, PDF"
              value={vhod}
              onChange={setVhod}
              min={0}
              decimalScale={4}
              decimalSeparator=","
              hideControls
              error={napake.vhod}
            />
            <NumberInput
              size="md"
              label="Izhod (USD / 1M)"
              description="Z razmišljanjem"
              value={izhod}
              onChange={setIzhod}
              min={0}
              decimalScale={4}
              decimalSeparator=","
              hideControls
              error={napake.izhod}
            />
          </SimpleGrid>
          <Text size="xs" c="dimmed">
            Ceno prepišite z{' '}
            <Anchor href="https://ai.google.dev/gemini-api/docs/pricing" target="_blank" rel="noreferrer" size="xs">
              Googlovega cenika
            </Anchor>{' '}
            (Standard, »Input price« za besedilo/slike in »Output price«; pri slikovnih modelih ceno izhoda za slike –
            ena slika je okoli 1.100–2.500 izhodnih žetonov).
          </Text>
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group>
            <Button onClick={() => void shrani()} loading={dela}>
              Shrani ceno
            </Button>
          </Group>
        </Stack>
      </SimpleGrid>
    </Kartica>
  );
}

/** Skrbništvo → Umetna inteligenca → funkcije za stranke: model, navodila in preizkus vsake funkcije. */
export function FunkcijeAi({ modeli }: { modeli?: Preverjanje }) {
  const funkcije = useQuery({
    queryKey: [...KLJUC_AI, 'funkcije'],
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai/funkcije', { headers: glave() }))),
  });
  if (!funkcije.data) {
    return (
      <Kartica naslov="Navodila funkcij">
        <StanjeNalaganja
          napaka={funkcije.error}
          ponovi={() => void funkcije.refetch()}
          naslov="Funkcij ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  return (
    <>
      {funkcije.data.map((f) => (
        <NavodilaFunkcije
          key={`${f.funkcija}-${f.spremenjeno ?? 'privzeta'}-${f.model ?? ''}-${f.velikostSlike ?? ''}`}
          f={f}
          modeli={modeli}
        />
      ))}
    </>
  );
}

const VELIKOSTI = [
  { value: '1K', label: '1K (okoli 1024 px)' },
  { value: '2K', label: '2K (okoli 2048 px)' },
  { value: '4K', label: '4K (okoli 4096 px)' },
];

/**
 * Model funkcije (prazno = model povezave; funkcija s slikami potrebuje slikovni model) in velikost izhodne slike.
 * Model se ob shranjevanju preizkusi pri ponudniku.
 */
function ModelFunkcije({ f, modeli, osvezi }: { f: Funkcija; modeli?: Preverjanje; osvezi: (f: Funkcija) => void }) {
  const [model, setModel] = useState<string | null>(f.model ?? null);
  const [velikost, setVelikost] = useState<string | null>(f.velikostSlike ?? null);
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const cenik = useQuery({
    queryKey: [...KLJUC_AI, 'cenik'],
    queryFn: () => klic(() => podatki(api.GET('/api/skrbnik/ai/cenik', { headers: glave() }))),
  });
  const spremenjeno = (model ?? null) !== (f.model ?? null) || (velikost ?? null) !== (f.velikostSlike ?? null);
  const brezCene = f.model && cenik.data && !cenik.data.some((c) => c.model === f.model);

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      osvezi(
        await podatki(
          api.PUT('/api/skrbnik/ai/funkcije/{funkcija}/nastavitve', {
            headers: glave(),
            params: { path: { funkcija: f.funkcija! } },
            body: { model: model ?? undefined, velikostSlike: velikost ?? undefined },
          }),
        ),
      );
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="sm">
      {f.slike && !f.model && (
        <Alert color="red" variant="light" role="alert">
          Funkcija vrača slike, zato potrebuje slikovni model. Dokler ga ne izberete, je stranke ne morejo uporabljati.
        </Alert>
      )}
      {brezCene && (
        <Alert color="red" variant="light" role="alert">
          Cena modela {f.model} ni vpisana v ceniku – stranke funkcije ne morejo uporabljati.
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, sm: f.slike ? 2 : 1 }} spacing="sm">
        <Select
          size="md"
          label={f.slike ? 'Slikovni model' : 'Model'}
          description={f.slike ? 'Obvezen (npr. Nano Banana).' : 'Prazno = model povezave.'}
          placeholder={f.slike ? 'Izberite slikovni model' : 'Model povezave'}
          data={izbireModelov(modeli, [model ?? undefined, f.model], f.slike ? 'slike' : 'besedilo')}
          value={model}
          onChange={setModel}
          searchable
          clearable
          error={napake.model}
        />
        {f.slike && (
          <Select
            size="md"
            label="Velikost slike"
            description="Prazno = privzeta modela."
            placeholder="Privzeta modela"
            data={VELIKOSTI}
            value={velikost}
            onChange={setVelikost}
            clearable
            error={napake.velikostSlike}
          />
        )}
      </SimpleGrid>
      {f.slike && (
        <Text size="xs" c="dimmed">
          Razmerje stranic je vedno kot pri izvirniku. Strežnik urejeno sliko shrani v največ 1600 px, zato je 1K ali 2K
          dovolj; večja velikost je dražja in počasnejša. Velikost podpirajo samo novejši modeli.
        </Text>
      )}
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group>
        <Button variant="light" onClick={() => void shrani()} loading={dela} disabled={!spremenjeno}>
          Shrani model
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Sistemska navodila funkcije: urejanje, ponastavitev na privzeta, struktura odgovora (določena v kodi) in preizkus na
 * datotekah z navodili iz polja (tudi neshranjenimi) – pokaže rezultat, kot ga dobi stranka, in surovi odgovor.
 */
function NavodilaFunkcije({ f, modeli }: { f: Funkcija; modeli?: Preverjanje }) {
  const odjemalec = useQueryClient();
  const [navodila, setNavodila] = useState(f.navodila ?? '');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<'shrani' | 'ponastavi' | 'preizkus' | null>(null);
  const [datoteke, setDatoteke] = useState<File[]>([]);
  const [model, setModel] = useState<string | null>(null);
  const [rezultat, setRezultat] = useState<Shema['AiPreizkusOdgovor'] | null>(null);
  const spremenjeno = navodila !== (f.navodila ?? '');

  const izvedi = async (kaj: 'shrani' | 'ponastavi' | 'preizkus', k: () => Promise<void>) => {
    setDela(kaj);
    setNapake({});
    setNapaka(null);
    try {
      await k();
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(null);
    }
  };

  const osvezi = (nova: Funkcija) =>
    odjemalec.setQueryData<Funkcija[]>([...KLJUC_AI, 'funkcije'], (s) =>
      (s ?? []).map((x) => (x.funkcija === nova.funkcija ? nova : x)),
    );

  const shrani = () =>
    izvedi('shrani', async () => {
      osvezi(
        await podatki(
          api.PUT('/api/skrbnik/ai/funkcije/{funkcija}', {
            headers: glave(),
            params: { path: { funkcija: f.funkcija! } },
            body: { navodila },
          }),
        ),
      );
    });

  const ponastavi = () =>
    izvedi('ponastavi', async () => {
      osvezi(
        await podatki(
          api.DELETE('/api/skrbnik/ai/funkcije/{funkcija}', {
            headers: glave(),
            params: { path: { funkcija: f.funkcija! } },
          }),
        ),
      );
    });

  const preizkusi = () =>
    izvedi('preizkus', async () => {
      if (datoteke.length === 0) {
        setNapake({ datoteke: 'Dodajte fotografijo ali PDF dokumenta.' });
        return;
      }
      const telo = new FormData();
      datoteke.forEach((d) => telo.append('datoteke', d));
      telo.append('navodila', navodila);
      if (model) {
        telo.append('model', model);
      }
      setRezultat(
        await podatki(
          api.POST('/api/skrbnik/ai/funkcije/{funkcija}/preizkus', {
            headers: glave(),
            params: { path: { funkcija: f.funkcija! } },
            body: telo as unknown as { datoteke: string[] },
          }),
        ),
      );
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_AI, 'klici'] });
    });

  return (
    <Kartica
      naslov={`Navodila: ${f.naziv}`}
      dejanja={
        f.privzeta ? (
          <Badge color="gray" variant="light">
            privzeta
          </Badge>
        ) : (
          <Badge color="indigo" variant="light">
            prilagojena
          </Badge>
        )
      }
    >
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {f.opis}
            {!f.privzeta && f.spremenil ? ` Spremenil ${f.spremenil}, ${cas(f.spremenjeno)}.` : ''}
          </Text>
          <ModelFunkcije f={f} modeli={modeli} osvezi={osvezi} />
          <Textarea
            size="md"
            label="Sistemska navodila"
            value={navodila}
            onChange={(e) => setNavodila(e.currentTarget.value)}
            autosize
            minRows={10}
            maxRows={28}
            error={napake.navodila}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontSize: 13 } }}
          />
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group gap="xs">
            <Button onClick={() => void shrani()} loading={dela === 'shrani'} disabled={!spremenjeno || dela !== null}>
              Shrani navodila
            </Button>
            {!f.privzeta && (
              <Button
                variant="subtle"
                color="red"
                onClick={() => void ponastavi()}
                loading={dela === 'ponastavi'}
                disabled={dela !== null}
              >
                Ponastavi na privzeta
              </Button>
            )}
          </Group>
          {f.slike ? (
            <Spoiler maxHeight={0} showLabel="Pokaži, kako je zahteva sestavljena" hideLabel="Skrij">
              <Text size="xs" c="dimmed" mb={4}>
                Besedila izbir določa koda, zato so tu samo za ogled; dopolnite ali omejite jih s sistemskimi navodili.
              </Text>
              <Code block style={{ maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                {f.shema}
              </Code>
            </Spoiler>
          ) : (
            <Spoiler maxHeight={0} showLabel="Pokaži strukturo odgovora (JSON Schema)" hideLabel="Skrij strukturo">
              <Text size="xs" c="dimmed" mb={4}>
                Strukturo določa koda (strežnik jo prebere in preveri), zato je tu samo za ogled. Imena polj lahko
                uporabite v navodilih.
              </Text>
              <Code block style={{ maxHeight: 420, overflow: 'auto' }}>
                {f.shema}
              </Code>
            </Spoiler>
          )}
        </Stack>
        {f.funkcija === 'UREJANJE_SLIK' ? (
          <PreizkusUrejanjaSlik f={f} navodila={navodila} modeli={modeli} />
        ) : f.funkcija === 'ASISTENT' || f.funkcija === 'ASISTENT_KOMENTAR' ? (
          <Text size="sm" c="dimmed">
            Asistenta preizkusite v aplikaciji (gumb Asistent v glavi); zahteve, ki jih ne zna, so v zavihku Asistent.
            Shranjena navodila veljajo takoj, pravila in orodja pa so v kodi in jih tu ni mogoče spremeniti.
          </Text>
        ) : f.funkcija !== 'BRANJE_DOKUMENTOV' ? (
          <Text size="sm" c="dimmed">
            To funkcijo preizkusite pri vozilu (Vozila → vozilo → Uredi → Ustvari opis), kjer so podatki in fotografije
            vozila. Shranjena navodila veljajo takoj.
          </Text>
        ) : (
          <Stack gap="sm">
            <Text size="sm" fw={600}>
              Preizkus z navodili iz polja (tudi neshranjenimi)
            </Text>
            <FileInput
              size="md"
              multiple
              label="Dokumenti"
              description="Do 5 slik ali PDF, skupaj 15 MB"
              placeholder="Izberite dokumente"
              accept="image/*,application/pdf"
              leftSection={<IconPaperclip size={16} />}
              value={datoteke}
              onChange={(f) => void pomanjsajVse(f).then(setDatoteke)}
              clearable
              error={napake.datoteke}
            />
            <Select
              size="md"
              label="Model"
              description="Prazno = shranjeni model."
              placeholder="Shranjeni model"
              data={izbireModelov(modeli)}
              value={model}
              onChange={setModel}
              searchable
              clearable
            />
            <Group>
              <Button
                leftSection={<IconSend size={18} />}
                onClick={() => void preizkusi()}
                loading={dela === 'preizkus'}
                disabled={dela !== null && dela !== 'preizkus'}
              >
                Preizkusi
              </Button>
            </Group>
            {rezultat && (
              <Stack gap={6}>
                <Text size="xs" c="dimmed">
                  {rezultat.model} · {stevilo(rezultat.vhodniZetoni ?? 0)} vhodnih +{' '}
                  {stevilo(rezultat.izhodniZetoni ?? 0)} izhodnih žetonov · {trajanje(rezultat.trajanjeMs)}
                  {rezultat.strosek != null ? ` · strošek ${tocke(rezultat.strosek)} USD` : ' · cena modela ni vpisana'}
                </Text>
                <Text size="sm" fw={600}>
                  Kot ga dobi stranka
                </Text>
                <Code block data-testid="rezultat-funkcije" style={{ maxHeight: 420, overflow: 'auto' }}>
                  {rezultat.besedilo}
                </Code>
                <Spoiler maxHeight={0} showLabel="Pokaži surovi odgovor modela" hideLabel="Skrij surovi odgovor">
                  <Code block style={{ maxHeight: 320, overflow: 'auto' }}>
                    {rezultat.surovo}
                  </Code>
                </Spoiler>
              </Stack>
            )}
          </Stack>
        )}
      </SimpleGrid>
    </Kartica>
  );
}

type Ozadje = 'NESPREMENJENO' | 'STUDIO' | 'LASTNO';

/**
 * Preizkus urejanja slik z navodili iz polja (tudi neshranjenimi): fotografija vozila, neobvezno logotip in lastno
 * ozadje ter izbire in navodila, kot jih da podjetje; rezultat je urejena slika (primerjava prej/potem), kot jo dobi
 * stranka. Ne zaračuna se, je v evidenci klicev.
 */
function PreizkusUrejanjaSlik({ f, navodila, modeli }: { f: Funkcija; navodila: string; modeli?: Preverjanje }) {
  const odjemalec = useQueryClient();
  const [foto, setFoto] = useState<File | null>(null);
  const [logotip, setLogotip] = useState<File | null>(null);
  const [ozadjeSlika, setOzadjeSlika] = useState<File | null>(null);
  const [ozadje, setOzadje] = useState<Ozadje>('STUDIO');
  const [tablica, setTablica] = useState(true);
  const [predmeti, setPredmeti] = useState(true);
  const [svetloba, setSvetloba] = useState(true);
  const [navodilaPodjetja, setNavodilaPodjetja] = useState('');
  const [navodilaSlike, setNavodilaSlike] = useState('');
  const [model, setModel] = useState<string | null>(null);
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [rezultat, setRezultat] = useState<{ odgovor: Shema['AiPreizkusOdgovor']; prej: string } | null>(null);

  const preizkusi = async () => {
    setNapake({});
    setNapaka(null);
    if (!foto) {
      setNapake({ datoteke: 'Dodajte fotografijo vozila.' });
      return;
    }
    setDela(true);
    try {
      const telo = new FormData();
      telo.append('datoteke', foto);
      if (logotip) {
        telo.append('logotip', logotip);
      }
      if (ozadje === 'LASTNO' && ozadjeSlika) {
        telo.append('ozadje', ozadjeSlika);
      }
      telo.append('vrstaOzadja', ozadje);
      telo.append('tablica', String(tablica));
      telo.append('predmeti', String(predmeti));
      telo.append('svetloba', String(svetloba));
      telo.append('navodilaPodjetja', navodilaPodjetja);
      telo.append('navodilaSlike', navodilaSlike);
      telo.append('navodila', navodila);
      if (model) {
        telo.append('model', model);
      }
      const odgovor = await podatki(
        api.POST('/api/skrbnik/ai/funkcije/{funkcija}/preizkus', {
          headers: glave(),
          params: { path: { funkcija: f.funkcija! } },
          body: telo as unknown as { datoteke: string[] },
        }),
      );
      setRezultat((r) => {
        if (r) {
          URL.revokeObjectURL(r.prej);
        }
        return { odgovor, prej: URL.createObjectURL(foto) };
      });
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_AI, 'klici'] });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  const slika = (nastavi: (f: File | null) => void) => (d: File | null) =>
    void (d ? pomanjsaj(d).then(nastavi) : Promise.resolve(nastavi(null)));

  return (
    <Stack gap="sm">
      <Text size="sm" fw={600}>
        Preizkus z navodili iz polja (tudi neshranjenimi)
      </Text>
      <FileInput
        size="md"
        label="Fotografija vozila"
        placeholder="Izberite fotografijo"
        accept="image/jpeg,image/png"
        leftSection={<IconPhoto size={16} />}
        value={foto}
        onChange={slika(setFoto)}
        clearable
        error={napake.datoteke}
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <FileInput
          size="md"
          label="Logotip podjetja"
          description="Za tablico; brez = prazna tablica."
          placeholder="Brez logotipa"
          accept="image/jpeg,image/png"
          leftSection={<IconPaperclip size={16} />}
          value={logotip}
          onChange={slika(setLogotip)}
          clearable
          error={napake.logotip}
        />
        <Select
          size="md"
          label="Ozadje"
          description="Studio ali fotografija podjetja."
          data={[
            { value: 'NESPREMENJENO', label: 'Nespremenjeno' },
            { value: 'STUDIO', label: 'Studio' },
            { value: 'LASTNO', label: 'Lastno ozadje (fotografija)' },
          ]}
          value={ozadje}
          onChange={(v) => setOzadje((v as Ozadje) ?? 'NESPREMENJENO')}
          allowDeselect={false}
        />
      </SimpleGrid>
      {ozadje === 'LASTNO' && (
        <FileInput
          size="md"
          label="Fotografija ozadja"
          placeholder="Npr. prodajni salon"
          accept="image/jpeg,image/png"
          leftSection={<IconPhoto size={16} />}
          value={ozadjeSlika}
          onChange={slika(setOzadjeSlika)}
          clearable
          error={napake.ozadje}
        />
      )}
      <Stack gap={6}>
        <Checkbox
          label="Registrsko tablico zamenjaj z logotipom"
          checked={tablica}
          onChange={(e) => setTablica(e.currentTarget.checked)}
        />
        <Checkbox
          label="Odstrani moteče predmete"
          checked={predmeti}
          onChange={(e) => setPredmeti(e.currentTarget.checked)}
        />
        <Checkbox
          label="Izboljšaj svetlobo"
          checked={svetloba}
          onChange={(e) => setSvetloba(e.currentTarget.checked)}
        />
        {napake.izbire && (
          <Text size="sm" c="red">
            {napake.izbire}
          </Text>
        )}
      </Stack>
      <Textarea
        size="md"
        label="Navodila podjetja"
        description="Kot jih stranka vpiše v nastavitvah."
        value={navodilaPodjetja}
        onChange={(e) => setNavodilaPodjetja(e.currentTarget.value)}
        autosize
        minRows={2}
        error={napake.navodilaPodjetja}
      />
      <Textarea
        size="md"
        label="Navodila za to sliko"
        value={navodilaSlike}
        onChange={(e) => setNavodilaSlike(e.currentTarget.value)}
        autosize
        minRows={2}
        error={napake.navodila}
      />
      <Select
        size="md"
        label="Model"
        description="Prazno = model funkcije."
        placeholder={f.model ?? 'Model funkcije'}
        data={izbireModelov(modeli, [], 'slike')}
        value={model}
        onChange={setModel}
        searchable
        clearable
        error={napake.model}
      />
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group>
        <Button leftSection={<IconSend size={18} />} onClick={() => void preizkusi()} loading={dela}>
          Uredi sliko
        </Button>
        {dela && (
          <Text size="sm" c="dimmed">
            Urejanje traja do pol minute …
          </Text>
        )}
      </Group>
      {rezultat && (
        <Stack gap={6}>
          <Text size="xs" c="dimmed">
            {rezultat.odgovor.model} · {stevilo(rezultat.odgovor.vhodniZetoni ?? 0)} vhodnih +{' '}
            {stevilo(rezultat.odgovor.izhodniZetoni ?? 0)} izhodnih žetonov · {trajanje(rezultat.odgovor.trajanjeMs)}
            {rezultat.odgovor.strosek != null
              ? ` · strošek ${tocke(rezultat.odgovor.strosek)} USD`
              : ' · cena modela ni vpisana'}
          </Text>
          {rezultat.odgovor.slika ? (
            <PrejPotem prej={rezultat.prej} potem={rezultat.odgovor.slika} />
          ) : (
            <Alert color="yellow" variant="light">
              {rezultat.odgovor.besedilo}
            </Alert>
          )}
          {rezultat.odgovor.slika && (
            <Text size="xs" c="dimmed" data-testid="rezultat-funkcije">
              {rezultat.odgovor.besedilo}
            </Text>
          )}
          <Spoiler maxHeight={0} showLabel="Pokaži besedilo modela" hideLabel="Skrij besedilo modela">
            <Code block style={{ whiteSpace: 'pre-wrap' }}>
              {rezultat.odgovor.surovo}
            </Code>
          </Spoiler>
        </Stack>
      )}
    </Stack>
  );
}
